import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useHistory, useLocation } from 'react-router-dom'
import { FleetGrid } from '../../../../shared/sso-workspace/fleet-grid'
import { Notice } from '../../../../shared/sso-workspace/fleet-page'
import { Pill } from '../../../../shared/sso-workspace/pill'
import { formatAgo, formatDateTime, serverMessage } from '../../../../shared/sso-workspace/format'
import { TableAgInfinite } from '../../../../shared/table-builder/table-ag-infinite'
import { ROW_HEIGHT_TWO_LINE } from '../../../../shared/table-builder/table-ag'
import { ColumnFormat } from '../../../../shared/table-builder/table-columns'
import { IconList } from '../../../../shared/icon/icon'
import { listRoutes, listRuns, listTargets } from './alert-routing-api'
import { useCatalog } from './catalog'
import { ALERT_STATUS, SEVERITY, WordPill, deliveriesText } from './run-words'
import { ErrorReference } from '../../../../shared/error/error-reference'
import { useErrorLine } from '../../../../shared/error/use-error-line'

export { deliveriesText }

const PAGE = 200

// A date input's value ("2026-10-01") as the start of that day in the
// viewer's own time zone; `to` is the start of the day after, so the day
// itself is included.
const localDay = (v, plusDays = 0) => {
  if (!v) return ''
  const [y, m, d] = v.split('-').map(Number)
  return new Date(y, m - 1, d + plusDays).toISOString()
}

// runsPager pages GET /runs for the grid: the server pages by cursor
// (`next`), the grid by row index, so each block start remembers the cursor
// that reaches it. A short page is the last one even if the server has more
// (the next block would have no cursor at its start).
export const runsPager = (filters, setError) => {
  const cursors = { 0: '' }
  return async (start, end) => {
    const before = cursors[start]
    if (before === undefined) return { data: [], count: start }
    try {
      setError(null)
      const limit = Math.min(end - start, PAGE)
      const page = await listRuns({ ...filters, from: localDay(filters.from), to: localDay(filters.to, 1), before, limit })
      const items = (page && page.items) || []
      if (page && page.next && items.length === limit) {
        cursors[start + items.length] = page.next
        return { data: items, count: -1 }
      }
      return { data: items, count: start + items.length }
    } catch (err) {
      setError(serverMessage(err, "Run history couldn't be loaded."), err)
      return { data: [], count: start }
    }
  }
}

const Select = ({ id, label, value, onChange, children }) =>
  <div className="ar-filter">
    <label className="sw-label" htmlFor={id}>{label}</label>
    <select id={id} className="form-select" value={value} onChange={onChange}><option value="">All</option>{children}</select>
  </div>

const BLANK = { category: '', trigger: '', status: '', routeID: '', targetID: '', from: '', to: '' }

export const RunsTab = () => {
  const catalog = useCatalog()
  const history = useHistory()
  const subjectRef = new URLSearchParams(useLocation().search).get('subjectRef') || ''
  const [f, setF] = useState(BLANK)
  const [routes, setRoutes] = useState([])
  const [targets, setTargets] = useState([])
  const [error, failure, setError] = useErrorLine()
  const tableRef = useRef()
  useEffect(() => { listRoutes().then(v => setRoutes(v || []), () => {}); listTargets().then(v => setTargets(v || []), () => {}) }, [])
  const filters = useMemo(() => ({ ...f, subjectRef }), [f, subjectRef])
  const getRows = useMemo(() => runsPager(filters, setError), [filters, setError])
  // A filter change is a new query, so it also clears a failure and retries.
  const set = k => e => { const v = e.target.value; setError(null); setF(x => ({ ...x, [k]: v })) }
  const triggers = f.category ? catalog.triggers.filter(t => t.category === f.category) : catalog.triggers
  return (
    <>
      {subjectRef && (
        <Notice tone="info">Showing the alerts about <code>{subjectRef}</code>. <Link to="/admin/alert-routing?tab=runs">Show every alert</Link></Notice>
      )}
      <div className="ar-filters mb-2">
        <Select id="ar-r-cat" label="Category" value={f.category} onChange={e => { const v = e.target.value; setError(null); setF(x => ({ ...x, category: v, trigger: '' })) }}>{catalog.categories.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}</Select>
        <Select id="ar-r-trig" label="Alert" value={f.trigger} onChange={set('trigger')}>{triggers.map(t => <option key={t.kind} value={t.kind}>{t.label}</option>)}</Select>
        <Select id="ar-r-status" label="Status" value={f.status} onChange={set('status')}>{Object.entries(ALERT_STATUS).map(([k, w]) => <option key={k} value={k}>{w.label}</option>)}</Select>
        <Select id="ar-r-route" label="Route" value={f.routeID} onChange={set('routeID')}>{routes.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}</Select>
        <Select id="ar-r-target" label="Target" value={f.targetID} onChange={set('targetID')}>{targets.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}</Select>
        <div className="ar-filter">
          <label className="sw-label" htmlFor="ar-r-from">From</label>
          <input id="ar-r-from" type="date" className="form-control" value={f.from} max={f.to || undefined} onChange={set('from')} />
        </div>
        <div className="ar-filter">
          <label className="sw-label" htmlFor="ar-r-to">To</label>
          <input id="ar-r-to" type="date" className="form-control" value={f.to} min={f.from || undefined} onChange={set('to')} />
        </div>
      </div>
      {/* A failure shows only its notice ("No alerts" under it would be
          untrue). The grid is unmounted, so Try again (or a filter change)
          mounts it afresh and it loads again. */}
      {error && (
        <Notice tone="bad" role="alert">{error} <button type="button" className="btn btn-sm sw-btn ms-1" onClick={() => setError(null)}>Try again</button><ErrorReference error={failure} /></Notice>
      )}
      {!error && <FleetGrid title="runs" subtitle="Newest first" tableRef={tableRef} rowHeight={ROW_HEIGHT_TWO_LINE}>
        <TableAgInfinite key={JSON.stringify(filters)} ref={tableRef} queryKey="" onGetRows={getRows} rowHeight={ROW_HEIGHT_TWO_LINE}
          onRowClicked={r => history.push(`/admin/alert-routing/runs/${r.id}`)}
          emptyState={{ Icon: IconList, title: 'No alerts', message: 'Nothing matches these filters.' }}>
          <ColumnFormat field="raisedAt" header="Raised" sortable={false} minWidth={140}
            render={r => (r.data ? <span title={formatDateTime(r.value)}>{formatAgo(r.value)}</span> : '')} />
          <ColumnFormat field="title" header="Alert" sortable={false} minWidth={260} flex={2} stacked
            render={r => (r.data ? <span><span className="fw-semibold">{r.value}</span><span className="sw-cell-sub">{r.data.triggerLabel || r.data.trigger}</span></span> : '')} />
          <ColumnFormat field="severity" header="Severity" sortable={false} minWidth={130} render={r => (r.data ? <WordPill words={SEVERITY} value={r.value} /> : '')} />
          <ColumnFormat field="organizationName" header="Organization" sortable={false} minWidth={140} render={r => (r.data ? r.value || '' : '')} />
          <ColumnFormat field="matchedRoutes" header="Routes" sortable={false} minWidth={80} render={r => (r.data ? r.value : '')} />
          <ColumnFormat field="sent" header="Deliveries" sortable={false} minWidth={230} render={r => (r.data ? deliveriesText(r.data) : '')} />
          <ColumnFormat field="status" header="Status" sortable={false} minWidth={200}
            render={r => (r.data ? <span className="ar-pills ar-nowrap"><WordPill words={ALERT_STATUS} value={r.value} />{r.data.isTest && <Pill tone="info">Test</Pill>}</span> : '')} />
        </TableAgInfinite>
      </FleetGrid>}
    </>
  )
}
